import type {State as ReduxState} from 'src/reducers';

import {useSelector} from 'react-redux';
import usePrivilege from 'src/hooks/usePrivilege';

import {selectReleaseFlags} from 'src/selectors/product-flags';
import {isInternalUser} from 'src/utils/check-internal-user';


export function useReleaseFlag(key: string): boolean | null | undefined {
  const releaseFlag = useSelector(selectReleaseFlags);
  return releaseFlag[key];
}

export function selectBooleanFlag(state: ReduxState, key: string): boolean {
  return Boolean(selectReleaseFlags(state)[key]);
}

/**
 * Use this flag when you want to check for internal users as well
 * If you just want to check for all users use selectBooleanFlag function
 */
export function selectBooleanFlagWithInternal(
  state: ReduxState,
  key: string,
  internalKey: string,
): boolean {
  return (
    selectBooleanFlag(state, key) ||
    (isInternalUser() && selectBooleanFlag(state, internalKey))
  );
}

export function useAnalyticsV2(): boolean {
  return useSelector(selectAnalyticsV2);
}

export function useAnalyticsResponseSummary(): boolean {
  const internalUser = isInternalUser();
  const analyticsResponseSummaryAiViewEnabled = useSelector(
    selectAnalyticsResponseSummaryAiViewEnabled,
  );
  const analyticsResponseSummaryAiViewEnabledInternal = useSelector(
    selectAnalyticsResponseSummaryAiViewEnabledInternal,
  );

  const canUseSurveySummaryGenerator = usePrivilege(
    'USE_SURVEY_SUMMARY_GENERATOR',
  );

  if (
    (analyticsResponseSummaryAiViewEnabled && canUseSurveySummaryGenerator) ||
    (internalUser && analyticsResponseSummaryAiViewEnabledInternal)
  ) {
    return true;
  }
  return false;
}

export function useListAuditEnabled(): boolean {
  return useSelector(selectListAuditEnabled);
}

export function useEventNoteWritebackActions(): boolean {
  return useSelector(selectEventNoteWritebackActions);
}
export function useCandidateMatchingFilterEnabled(): boolean {
  return useSelector(selectCandidateMatchingFilterEnabled);
}
export function useSchedulingV2(): boolean {
  return useSelector(selectSchedulingV2);
}
export function useChatbotSourcing(): boolean {
  return useSelector(selectChatbotSourcingEnabled);
}
export function useChatbotNodeVariables(): boolean {
  return useSelector(selectChatbotNodeVariablesEnabled);
}
export function useChatbotDataReview(): boolean {
  return useSelector(selectChatbotDataReviewEnabled);
}

export function useJobSearchAndMatch(): boolean {
  // (nilarnab): releaseFlag for discover is getting deprecated
  // please use product specific agency_config to check if discover is enabled
  return useSelector(selectJobSearchAndMatch);
}

export function useDiscoverJobSearchAnalyticsEnabled(): boolean {
  return useSelector(selectDiscoverJobSearchAnalyticsEnabled);
}

export function usePostApplicationAgentAnalytics(): boolean {
  return useSelector(selectPostApplicationAgentAnalytics);
}

export function useStarredThreads(): boolean {
  return useSelector(selectStarredThreads);
}

export function useContentSubscriptionV2Footer(): boolean {
  return useSelector(selectContentSubscriptionV2Footer);
}
export function useAudienceFilterSeparation(): boolean {
  return useSelector(selectAudienceFilterSeparation); // Note: this has been released for all.
}
export function useActionNowEnabled(): boolean {
  return useSelector(selectActionNowEnabled);
}

export function useServiceAccounts(): boolean {
  return useSelector(selectSchedulerServiceAccountsEnabled);
}

export function useSchedulerMultiEventAttendee(): boolean {
  return useSelector(selectSchedulerMultiEventAttendeeEnabled);
}

export function useChatbotReminders(): boolean {
  return useSelector(selectChatbotRemindersEnabled);
}

export function useNewAllowedAttributes(): boolean {
  return useSelector(selectNewAllowedAttributes);
}

export function useTRMEnabled(): boolean {
  const canEditAndViewTRM = usePrivilege('TRM_ADMIN');
  const canViewTRM = usePrivilege('TRM_VIEW');
  return useSelector(selectTRMEnabled) && (canEditAndViewTRM || canViewTRM);
}

export function useMultiEventChatbotMeetingNodes(): boolean {
  return useSelector(selectMultiEventChatbotMeetingNodes);
}

export function useScheduleCapacityManagement(): boolean {
  return useSelector(selectScheduleCapacityManagementEnabled);
}

export function useChatbotDatabaseMaintenance(): boolean {
  return useSelector(selectChatbotDatabaseMaintenance);
}

export function useSchedulerHolidyManagement(): boolean {
  return useSelector(selectSchedulerHolidyManagementEnabled);
}

export function useAbtRevamp(): boolean {
  return useSelector(selectAbtRevamp);
}

export function useSchedulerDailyDigest(): boolean {
  return useSelector(selectSchedulerDailyDigestEnabled);
}

export function useMessagingInboxDrawer(): boolean {
  return useSelector(selectMessagingInboxDrawer);
}

export function useLiveAgentTransfer(): boolean {
  return useSelector(selectLATEnabled);
}

export function useLinkToATS(): boolean {
  return useSelector(selectLinkToATSEnabled);
}

export function usePanelInterview(): boolean {
  return useSelector(selectPanelInterviewsEnabled);
}

export function useChatbotJobMatchWorksiteOptions(): boolean {
  return useSelector(selectChatbotJobMatchWorksiteOptions);
}

export function useChatbotJobMatchBrandLabel(): boolean {
  return useSelector(selectChatbotJobMatchBrandLabel);
}

export function useApplicationChatbot(): boolean {
  return useSelector(selectApplicationChatbotEnabled);
}

export function useAgentBuilderEnabled(): boolean {
  return useSelector(selectAgentBuilderEnabled);
}

export function useSchedulerFixedDate(): boolean {
  return useSelector(selectSchedulerFixedDateEnabled);
}

export function useSchedulerAvailabilityTimeBlock(): boolean {
  return useSelector(selectSchedulerAvailabilityTimeBlockEnabled);
}

export function useSchedulerWhatsappNotification(): boolean {
  return useSelector(selectSchedulerWhatsappNotificationEnabled);
}

export function useSchedulerVirtualCalendar(): boolean {
  return useSelector(selectSchedulerVirtualCalendarEnabled);
}

export function useChatbotFaqV2(): boolean {
  return useSelector(selectUsesChatbotFaqV2);
}

export function useChatbotV1Disabled(): boolean {
  return useSelector(selectChatbotV1Disabled);
}

export function useChatbotLiveFlowEditEnabled(): boolean {
  return useSelector(selectChatbotLiveFlowEditEnabled);
}

export function useAppShellEnabled(): boolean {
  return useSelector(selectAppShellEnabled);
}

export function useEnableAdditionalEntities(): boolean {
  return useSelector(selectEnableAdditionalEntities);
}

export function useRecordsEnabled(): boolean {
  return useSelector(recordsEnabled);
}

export function useTrmAppShellEnabled(): boolean {
  return useSelector(selectTrmAppShellEnabled);
}

export function useCandidateRoiEnabled(): boolean {
  return useSelector(selectCandidateRoiEnabled);
}

export function useAnalyticsDiscoverEnhancementEnabled(): boolean {
  return useSelector(selectAnalyticsDiscoverEnhancementEnabled);
}

export function useAnalyticsNPSDashboardViewEnabled(): boolean {
  return useSelector(selectAnalyticsNPSDashboardViewEnabled);
}

export function useAnalyticsSurveysDashboardViewEnabled(): boolean {
  return useSelector(selectAnalyticsSurveysDashboardViewEnabled);
}

export function useAnalyticsCrmCandidateSourceViewEnabled(): boolean {
  return useSelector(selectAnalyticsCrmCandidateSourceViewEnabled);
}

export function useExportAndBulkMemberCount(): boolean {
  return useSelector(selectExportAndBulkMemberCountEnabled);
}

export function useReferralsPaypalPayment(): boolean {
  return useSelector(selectReferralsPaypalPayment);
}

export function useCanadaPostalCodesEnabled(): boolean {
  return useSelector(selectCanadaPostalCodesEnabled);
}

export function useCSVListEnabled(): boolean {
  return useSelector(selectCSVListEnabled);
}

export function useCrmMailbox(): boolean {
  return useSelector(selectCrmMailbox);
}

export function useTalentPoolEnabled(): boolean {
  return useSelector(selectTalentPoolEnabled);
}

export function useChannelEngagementForSurveysEnabled(): boolean {
  return useSelector(selectChannelEngagementForSurveysEnabled);
}

export function useChannelEngagementForNPSEnabled(): boolean {
  return useSelector(selectChannelEngagementForNPSEnabled);
}

export function useSecurityGroupFilterValidationEnabled(): boolean {
  return useSelector(SelectSecurityGroupFilterValidationEnabled);
}

export function useDiscoverActiveJobsViewEnabled(): boolean {
  return useSelector(selectDiscoverActiveJobsViewEnabled);
}

export function useJ1ExportJourneysWithTouchpoints(): boolean {
  return useSelector(selectJ1ExportJourneysWithTouchpoints);
}

export function useJ2ExportWorkflowsAllNodesRecipients(): boolean {
  return useSelector(selectJ2ExportWorkflowsAllNodesRecipients);
}

export function useConversationTabEnabled(): boolean {
  return useSelector(selectConversationTabEnabled);
}

export function selectListAuditEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'listAuditEnabled');
}

export function selectConversationTabEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'conversationsTabEnabled');
}

export function SelectSecurityGroupFilterValidationEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'securityGroupFilterValidation');
}

export function selectContentSubscriptionV2Footer(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'contentSubscriptionV2Footer');
}

export function selectStarredThreads(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'messagingStarredThreads');
}

export function selectAgentBuilderEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotAgentBuilderEnabled');
}

export function selectWorkflowActivationV2(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'workflowActivationV2');
}

export function selectSpamBotPreventionWithDoubleClick(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'spamBotPreventionWithDoubleClick');
}

export function selectAnalyticsV2(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'analyticsV2');
}
export function selectAnalyticsResponseSummaryAiViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'analyticsResponseSummaryAiViewEnabled');
}
export function selectAnalyticsResponseSummaryAiViewEnabledInternal(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(
    state,
    'analyticsResponseSummaryAiViewEnabledInternal',
  );
}
export function selectSchedulingV2(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'schedulingV2');
}
export function selectJobSearchAndMatch(state: ReduxState): boolean {
  // (nilarnab): releaseFlag for discover is getting deprecated
  // please use product specific agency_config to check if discover is enabled
  return selectBooleanFlag(state, 'jobSearchAndMatch');
}

export function selectPostApplicationAgentAnalytics(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'postApplicationAgentAnalytics',
    'postApplicationAgentAnalyticsInternal',
  );
}

export function selectDiscoverJobSearchAnalyticsEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'jobMatchingAnalyticsDashboardViewEnabled',
    'jobMatchingAnalyticsDashboardViewEnabledInternal',
  );
}
export function selectChatbotSourcingEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotSourcingEnabled');
}
export function selectChatbotNodeVariablesEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotNodeVarsEnabled');
}

export function selectChatbotDataReviewEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatobtDataReviewEnabled');
}
export function selectAudienceFilterSeparation(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'audienceFilterSeparation');
}
export function selectActionNowEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'actionNowEnabled');
}
export function selectCandidateMatchingFilterEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'candidateMatchingFilterEnabled');
}

export function selectSchedulerServiceAccountsEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'chatbotServiceAccount');
}

export function selectSchedulerMultiEventAttendeeEnabled(
  state: ReduxState,
): boolean {
  return Boolean(
    selectReleaseFlags(state)['enableSchedulerMultiEventAttendee'],
  );
}

export function selectChatbotRemindersEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotRemindersEnabled');
}

// TODO(Anant): this flag is released for all. code cleanup needed.
export function selectNewAllowedAttributes(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotNewAllowedAttrs');
}

export function selectTRMEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'trmEnabled');
}

export function selectMessagingInboxDrawer(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'showMessagingInboxDrawer');
}

export function selectOldestEventTimeEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'messagingThreadsOldestEventTime');
}

export function selectEventNoteWritebackActions(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'eventNoteWritebackActions');
}

export function selectMultiEventChatbotMeetingNodes(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'enableSchedulerMultiEventChatbot');
}

export function selectScheduleCapacityManagementEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'enableScheduleCapacityManagement');
}

export function selectChatbotDatabaseMaintenance(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotDatabaseMaintenance');
}

export function selectSchedulerHolidyManagementEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'enableSchedulerHolidayManagement');
}

export function selectSigninSplashEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'signinSplashEnabled');
}

export function selectSigninSplashIsCorpAgency(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'signinSplashCorp');
}

export function selectAbtRevamp(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'abtRevamp');
}

export function selectSchedulerDailyDigestEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableSchedulerDailyDigest');
}

export function selectLATEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableLiveAgentTransfer');
}

export function selectLinkToATSEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'messagingViewAtsProfileLink');
}

export function selectPanelInterviewsEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableSchedulerPanelInterview');
}

export function selectChatbotJobMatchWorksiteOptions(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'chatbotJobMatchWorksiteOptions');
}

export function selectChatbotJobMatchBrandLabel(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotJobMatchBrandLabel');
}

export function selectApplicationChatbotEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotInboundApplicationBotWeb');
}

export function selectSchedulerFixedDateEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'schedulerEnableFixedDateScheduling');
}

export function selectSchedulerAvailabilityTimeBlockEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'schedulerAvailabilityTimeBlocks');
}

export function selectSchedulerWhatsappNotificationEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'schedulerEnableWhatsappNotification');
}

export function selectSchedulerVirtualCalendarEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'schedulerEnableVirtualCalendar');
}

export function selectUsesChatbotFaqV2(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotFaqV2');
}

export function selectChatbotV1Disabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotDisableV1Flow');
}

export function selectChatbotLiveFlowEditEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'chatbotLiveFlowEdit');
}

export function selectAppShellEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'appShell');
}

export function recordsEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableAllRecords');
}

export function selectTrmAppShellEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'trmAppShell');
}

export function selectMsgLanguageTranslationEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'messagingMessageTranslation');
}

export function selectExportAndBulkMemberCountEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'listExportAndBulkFilterCount');
}

export function selectCandidateRoiEnabled(state: ReduxState): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'candidateRoiEnabled',
    'candidateRoiEnabledInternal',
  );
}

export function selectAnalyticsDiscoverEnhancementEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsDiscoverEnhancementEnabled',
    'analyticsDiscoverEnhancementEnabledInternal',
  );
}

export function selectAnalyticsNPSDashboardViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsNpsDashboardViewEnabled',
    'analyticsNpsDashboardViewEnabledInternal',
  );
}

export function selectReferralsPaypalPayment(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'referralsPaypalPayment');
}

export function selectReferralJ2IdentifierEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableJ2Referral');
}

export function useReferralJ2IdentifierEnabled(): boolean {
  return useSelector(selectReferralJ2IdentifierEnabled);
}

export function selectCanadaPostalCodesEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableCanadaPostalCodes');
}

export function selectEnableAdditionalEntities(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableAdditionalEntities');
}

export function selectCSVListEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'csvListsEnabled');
}

export function selectChannelEngagementForNPSEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'channelEngagementForNpsEnabled');
}

export function selectAnalyticsSurveysDashboardViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsSurveysDashboardViewEnabled',
    'analyticsSurveysDashboardViewEnabledInternal',
  );
}

export function selectAnalyticsCrmCandidateSourceViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsCrmCandidateSourceViewEnabled',
    'analyticsCrmCandidateSourceViewEnabledInternal',
  );
}

export function selectDiscoverActiveJobsViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'discoverActiveJobsViewEnabled',
    'discoverActiveJobsViewEnabledInternal',
  );
}

export function selectDiscoverActiveCandidateJobLogicViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'discoverActiveCandidateJobLogicViewEnabled',
    'discoverActiveCandidateJobLogicInternalViewEnabled',
  );
}

// This is the old release flag for all schedule reports. It is released for all.
// Use selectAnalyticsScheduleAllReportViewEnabled instead.
export function selectAnalyticsScheduledReportsEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsScheduleReportViewEnabled',
    'analyticsScheduleReportViewEnabledInternal',
  );
}

// This is the new release flag for all schedule reports.
export function selectAnalyticsScheduleAllReportViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsScheduleAllReportViewEnabled',
    'analyticsScheduleAllReportViewEnabledInternal',
  );
}

export function selectAnalyticsExternalLinkClicksWorkflowsDashboardViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsExternalLinkClicksWorkflowsDashboardViewEnabled',
    'analyticsExternalLinkClicksWorkflowsDashboardViewEnabledInternal',
  );
}

export function selectAnalyticsSurveysV2DashboardViewEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsSurveysV2DashboardViewEnabled',
    'analyticsSurveysV2DashboardViewEnabledInternal',
  );
}

export function useAnalyticsSurveysV2DashboardViewEnabled(): boolean {
  return useSelector(selectAnalyticsSurveysV2DashboardViewEnabled);
}

export function selectByodEnabled(state: ReduxState): boolean {
  return selectBooleanFlagWithInternal(
    state,
    'analyticsByodEnabled',
    'analyticsByodEnabledInternal',
  );
}

export function useByodEnabled(): boolean {
  return useSelector(selectByodEnabled);
}

export function useDiscoverActiveCandidateJobLogicViewEnabled(): boolean {
  return useSelector(selectDiscoverActiveCandidateJobLogicViewEnabled);
}

export function useCrmRedesignedEnabled(): boolean {
  return Boolean(useReleaseFlag('crmRedesignEnabled'));
}

export function useCrmVoiceAiEnabled(): boolean {
  return Boolean(useReleaseFlag('crmVoiceAiIntegration'));
}

export function useCrmNewResumeParsing(): boolean {
  return Boolean(useReleaseFlag('crmNewResumeParsing'));
}

export function selectCrmMailbox(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'crmMailboxEnabled');
}

export function selectTalentPoolEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'talentPoolEnabled');
}

export function selectChannelEngagementForSurveysEnabled(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'channelEngagementForSurveysEnabled');
}

export function selectJ1ExportJourneysWithTouchpoints(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'j1ExportJourneysWithTouchpoints');
}

export function selectJ2ExportWorkflowsAllNodesRecipients(
  state: ReduxState,
): boolean {
  return selectBooleanFlag(state, 'j2ExportWorkflowsAllNodesRecipients');
}

export function selectSchedulerMajorFeatureEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'schedulerUiMajorFeatureRelease');
}

export function useSchedulerMajorFeatureEnabled(): boolean {
  return useSelector(selectSchedulerMajorFeatureEnabled);
}

// This is the old release flag hook for all schedule reports. It is released for all.
// Use useAnalyticsScheduleAllReportViewEnabled instead.
export function useAnalyticsScheduledReportsEnabled(): boolean {
  return useSelector(selectAnalyticsScheduledReportsEnabled);
}

// This is the new release flag hook for all schedule reports.
export function useAnalyticsScheduleAllReportViewEnabled(): boolean {
  return useSelector(selectAnalyticsScheduleAllReportViewEnabled);
}

export function useCrmKanbanViewEnabled(): boolean {
  return Boolean(useReleaseFlag('enableCrmApplicantKanbanView'));
}

export function selectVoiceAgentBuilderEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'voiceEnabledAgentBuilder');
}

export function useVoiceAgentBuilderEnabled(): boolean {
  return useSelector(selectVoiceAgentBuilderEnabled);
}

export function useAnalyticsExternalLinkClicksWorkflowsDashboardViewEnabled(): boolean {
  return useSelector(
    selectAnalyticsExternalLinkClicksWorkflowsDashboardViewEnabled,
  );
}

export function selectDataVariablePageV2Enabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'dataengVariablePageV2');
}

export function useDataVariablePageV2Enabled(): boolean {
  return useSelector(selectDataVariablePageV2Enabled);
}

export function selectFieldSyncEnabled(state: ReduxState): boolean {
  return selectBooleanFlag(state, 'enableFieldSyncAudit');
}

export function useFieldSyncEnabled(): boolean {
  return useSelector(selectFieldSyncEnabled);
}
